import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { parseMulti, joinMulti, filtrarOpcionSinAcentos } from '../lib/campos'
import type { FieldOption } from './SearchableSelect'

interface Props {
  id?: string
  placeholder: string
  options: FieldOption[]
  /** Valor coma-separado (ej. `"15,16"`); vacío/undefined = sin selección. */
  value: string | undefined
  /** Emite el string coma-unido, o `undefined` cuando no queda nada seleccionado. */
  onChange: (v: string | undefined) => void
}

/**
 * Combobox buscable de selección MÚLTIPLE. Hermano de `SearchableSelect`: misma
 * estructura (trigger tipo Select + CommandInput), pero cada clic alterna la
 * pertenencia del valor y el popover permanece abierto. El valor se codifica como
 * string coma-separado, que el backend expande a parámetros repetidos hacia el
 * upstream (ver `_split_multivalor`).
 */
export function MultiSearchableSelect({ id, placeholder, options, value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const seleccion = parseMulti(value)
  const seleccionSet = new Set(seleccion)

  const toggle = (val: string) => {
    const next = seleccionSet.has(val)
      ? seleccion.filter((v) => v !== val)
      : [...seleccion, val]
    onChange(joinMulti(next))
  }

  // Texto del trigger: placeholder (vacío) / label único (1) / "N seleccionados".
  const triggerLabel =
    seleccion.length === 0
      ? placeholder
      : seleccion.length === 1
        ? (options.find((o) => o.value === seleccion[0])?.label ?? seleccion[0])
        : `${seleccion.length} seleccionados`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background',
            'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <span className={cn('truncate', seleccion.length === 0 && 'text-muted-foreground')}>
            {triggerLabel}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={filtrarOpcionSinAcentos}>
          <CommandInput placeholder="Buscar..." className="h-9" />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onChange(undefined)
                  setOpen(false)
                }}
              >
                <Check
                  className={cn('mr-2 h-4 w-4', seleccion.length === 0 ? 'opacity-100' : 'opacity-0')}
                />
                {placeholder}
              </CommandItem>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => toggle(opt.value)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      seleccionSet.has(opt.value) ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
